// Repository: all reads/writes go through here so UI never touches Dexie directly.
// Saving a session also enqueues it to the outbox (idempotent by session id) so a
// later reconnect drains it to Notion.

import { db } from './db';
import type {
  Exercise,
  GymId,
  Routine,
  Session,
  BodyMeasurement,
  OutboxItem,
  MeasurementOutboxItem,
} from '../domain/types';
import type { ExerciseHistoryPoint } from '../domain/progression';
import { selectExerciseHistory, type HistoryOpts } from '../domain/history';
import { DEFAULT_GYM_ID, LAST_GYM_META_KEY, isGymId } from '../domain/gym';

function nowISO(): string {
  return new Date().toISOString();
}

export function newId(prefix: string): string {
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
  return `${prefix}-${rand}`;
}

// --- Exercises ---
export async function allExercises(includeArchived = false): Promise<Exercise[]> {
  const list = await db.exercises.toArray();
  const filtered = includeArchived ? list : list.filter((e) => !e.archived);
  return filtered.sort((a, b) => a.name.localeCompare(b.name));
}

export async function getExercise(id: string): Promise<Exercise | undefined> {
  return db.exercises.get(id);
}

export async function putExercise(ex: Exercise): Promise<void> {
  await db.exercises.put(ex);
}

export async function exerciseMap(): Promise<Map<string, Exercise>> {
  const list = await db.exercises.toArray();
  return new Map(list.map((e) => [e.id, e]));
}

// --- Routines ---
export async function allRoutines(): Promise<Routine[]> {
  const list = await db.routines.toArray();
  return list.sort((a, b) => (a.weekday ?? 99) - (b.weekday ?? 99));
}

export async function getRoutine(id: string): Promise<Routine | undefined> {
  return db.routines.get(id);
}

// --- Sessions ---
function byDateDesc(a: Session, b: Session): number {
  if (a.date !== b.date) return a.date < b.date ? 1 : -1;
  return a.createdAt < b.createdAt ? 1 : -1;
}

export async function allSessions(): Promise<Session[]> {
  const list = await db.sessions.toArray();
  return list.sort(byDateDesc);
}

// Finished sessions only (excludes in-progress drafts). Targets, volume, history,
// and export all read this.
export async function finishedSessions(): Promise<Session[]> {
  const list = await db.sessions.toArray();
  return list.filter((s) => s.finishedAt != null).sort(byDateDesc);
}

export async function draftSessions(): Promise<Session[]> {
  const list = await db.sessions.toArray();
  return list.filter((s) => s.finishedAt == null).sort(byDateDesc);
}

export async function getSession(id: string): Promise<Session | undefined> {
  return db.sessions.get(id);
}

// Most recent recorded bodyweight from a finished session, used only as the
// pre-fill when Marko chooses to add bodyweight to a new session (it's optional).
export async function lastBodyweightKg(): Promise<number | null> {
  const list = await finishedSessions();
  for (const s of list) if (typeof s.bodyweightKg === 'number') return s.bodyweightKg;
  return null;
}

// Persist an in-progress draft WITHOUT enqueuing to the outbox (drafts never sync).
export async function putDraft(session: Session): Promise<void> {
  await db.sessions.put({ ...session, updatedAt: nowISO() });
}

// Finish (or re-save) a session: stamp finishedAt and enqueue for sync. Resets the
// outbox row to pending so an edit re-syncs. Idempotent by session id.
export async function saveSession(session: Session): Promise<void> {
  const ts = nowISO();
  const toSave: Session = {
    ...session,
    updatedAt: ts,
    finishedAt: session.finishedAt ?? ts,
  };
  await db.transaction('rw', db.sessions, db.outbox, async () => {
    await db.sessions.put(toSave);
    await db.outbox.put({
      sessionId: toSave.id,
      status: 'pending',
      attempts: 0,
      lastError: null,
      updatedAt: ts,
    });
  });
}

export async function deleteSession(id: string): Promise<void> {
  await db.transaction('rw', db.sessions, db.outbox, async () => {
    await db.sessions.delete(id);
    await db.outbox.delete(id);
  });
}

// Most recent sessions that included a given exercise, newest first. Used by the
// progression engine ("beat this" + stall) and the history view.
//
// `gymId` scopes the result to one gym, and every planning path passes it: weights
// are only comparable within a gym, so mixing them would both mis-target loads and
// make the 3-session stall detector fire spuriously whenever gyms alternate. There
// is deliberately NO cross-gym fallback — the first session on an exercise at a new
// gym has no history, shows "log a baseline", and Marko types the weight once.
// Pass no gymId (trend charts, exports) to get the full cross-gym history.
export async function exerciseHistory(
  exerciseId: string,
  opts: HistoryOpts = {},
): Promise<ExerciseHistoryPoint[]> {
  const sessions = await finishedSessions(); // newest-first, drafts excluded
  return selectExerciseHistory(sessions, exerciseId, opts);
}

// --- Body measurements ---
export async function allMeasurements(): Promise<BodyMeasurement[]> {
  const list = await db.measurements.toArray();
  return list.sort((a, b) => (a.date < b.date ? 1 : -1));
}

// Save a measurement and enqueue it for sync (idempotent by id locally; the Worker
// upserts into Notion by date). Mirrors saveSession's outbox pattern.
export async function putMeasurement(m: BodyMeasurement): Promise<void> {
  const ts = nowISO();
  await db.transaction('rw', db.measurements, db.measurementOutbox, async () => {
    await db.measurements.put(m);
    await db.measurementOutbox.put({
      measurementId: m.id,
      status: 'pending',
      attempts: 0,
      lastError: null,
      updatedAt: ts,
    });
  });
}

export async function deleteMeasurement(id: string): Promise<void> {
  // Local-only delete (no Notion delete), same as sessions. Drop the outbox row too.
  await db.transaction('rw', db.measurements, db.measurementOutbox, async () => {
    await db.measurements.delete(id);
    await db.measurementOutbox.delete(id);
  });
}

export async function getMeasurement(id: string): Promise<BodyMeasurement | undefined> {
  return db.measurements.get(id);
}

export async function pendingMeasurementOutbox(): Promise<MeasurementOutboxItem[]> {
  return db.measurementOutbox.where('status').anyOf('pending', 'error').toArray();
}

export async function markMeasurementOutbox(
  measurementId: string,
  status: MeasurementOutboxItem['status'],
  lastError: string | null,
  attempts: number,
): Promise<void> {
  await db.measurementOutbox.put({ measurementId, status, lastError, attempts, updatedAt: nowISO() });
}

// --- Outbox ---
export async function pendingOutbox(): Promise<OutboxItem[]> {
  return db.outbox.where('status').anyOf('pending', 'error').toArray();
}

export async function markOutbox(
  sessionId: string,
  status: OutboxItem['status'],
  lastError: string | null,
  attempts: number,
): Promise<void> {
  await db.outbox.put({ sessionId, status, lastError, attempts, updatedAt: nowISO() });
}

export async function outboxCounts(): Promise<{ pending: number; error: number; synced: number }> {
  const all = await db.outbox.toArray();
  return {
    pending: all.filter((o) => o.status === 'pending').length,
    error: all.filter((o) => o.status === 'error').length,
    synced: all.filter((o) => o.status === 'synced').length,
  };
}

// --- Meta (key/value) ---
export async function getMeta(key: string): Promise<string | undefined> {
  const row = await db.meta.get(key);
  return row?.value;
}

export async function setMeta(key: string, value: string): Promise<void> {
  await db.meta.put({ key, value });
}

// --- Gym selection ---
// The gym picker pre-selects whatever was used last (he trains in blocks at one
// gym), falling back to the home gym on a fresh install.
export async function getLastGymId(): Promise<GymId> {
  const raw = await getMeta(LAST_GYM_META_KEY);
  return isGymId(raw) ? raw : DEFAULT_GYM_ID;
}

export async function setLastGymId(gymId: GymId): Promise<void> {
  await setMeta(LAST_GYM_META_KEY, gymId);
}

// Store coaching pulled back from Notion onto the matching session (Cowork-owned
// fields; the app only displays them, never pushes them).
export async function applyCoaching(
  sessionId: string,
  coaching: string | null,
  suggestedAdjustments: Session['suggestedAdjustments'],
  coachedAt: string | null,
): Promise<void> {
  const s = await db.sessions.get(sessionId);
  if (!s) return;
  await db.sessions.put({ ...s, coaching, suggestedAdjustments, coachedAt });
}
